import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { SetOptions, TextZoom } from '@capacitor/text-zoom';
import { SplashScreen } from '@ionic-native/splash-screen/ngx';
import { IonRouterOutlet, LoadingController, Platform } from '@ionic/angular';
import 'hammerjs';
import { Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { DebugSessionService } from './baseServices/logging/debug-session.service';
import { SyncService } from './baseServices/sync.service';
import { GlobalConstants } from './constant/constant';
import { AppEventType } from './enums/app-events.enum';
import { PUSH_NOTIFICATION_TYPES } from './enums/general.enum';
import { LOCAL_NOTIFICATION_ACTION_TYPE } from './enums/notification/notification.enum';
import { AlertService } from './services/alert.service';
import { AppStateService } from './services/app-state.service';
import { AppUserDailyTimeStatusService } from './services/app-user-daily-time-status.service';
import { AppUserTimeEntryMethodService } from './services/app-user-time-entry-method.service';
import { AppUserService } from './services/app-user.service';
import { AuthService } from './services/auth/auth.service';
import { DeviceInfoService } from './services/device-info.service';
import { EventBusService } from './services/event-bus.service';
import { KioskModeService } from './services/kiosk-mode.service';
import { LocationTrackingService } from './services/location-tracking.service';
import { MessageCenterService } from './services/message-center.service';
import { NotificationsService } from './services/notifications.service';
import { OdometerReadingMediaCleanupService } from './services/odometer-reading-media-cleanup.service';
import { ProjectBoundaryTrackingService } from './services/project-boundary-tracking.service';
import { RoleClaimService } from './services/role-claim.service';
import { TimeService } from './services/time.service';
import { ChatbotService } from './services/chatbot.service';

interface AppSideNavItem {
  title: string;
  url: string;
  ionicIcon?: string;
  hidden?: boolean;
};

interface SideMenuConfiguration {
  hideClockEntryItems: boolean;
  hideBlockEntryItems: boolean;
  hideReviewTeamTime: boolean;
  hideMap: boolean;
  hideVirtualBadge: boolean;
}

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['./side-menu/styles/side-menu.scss', './side-menu/styles/side-menu.shell.scss', './side-menu/styles/side-menu.responsive.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild(IonRouterOutlet, { static: true }) routerOutlet: IonRouterOutlet;
  notificationString: any;
  notificationdata: any;
  appPages: AppSideNavItem[] = [];
  appInfoPages: AppSideNavItem[] = [];
  appVersion: any;
  textDir = 'ltr';
  canReviewTeamTime: boolean = false;
  displaySidePane$: Observable<boolean> = this.appUserService.currentUser$.pipe(
    map(user => !this.deviceInfoService.checkIsMobile() && !!user),
    distinctUntilChanged()
  );

  private sideMenuConfiguration: SideMenuConfiguration = {
    hideClockEntryItems: true,
    hideBlockEntryItems: true,
    hideReviewTeamTime: true,
    hideMap: true, 
    hideVirtualBadge: true,
  }
  private destroy$ = new Subject<void>();
  private subscriptions: Subscription[] = [];

  // Inject HistoryHelperService in the app.components.ts so its available app-wide
  constructor(
    private appStateService: AppStateService,
    private splashScreen: SplashScreen,
    private platform: Platform,
    private router: Router,
    private notificationsService: NotificationsService,
    private location: Location,
    private messageService: MessageCenterService,
    private syncService: SyncService,
    private timeService: TimeService,
    private authService: AuthService,
    private eventBusService: EventBusService,
    private loadingController: LoadingController,
    private kioskModeService: KioskModeService,
    private roleClaimService: RoleClaimService,
    private deviceInfoService: DeviceInfoService,
    private appUserService: AppUserService,
    private appUserTimeEntryMethodService: AppUserTimeEntryMethodService,
    // Injecting below stateful services in the app.components.ts so they get constructed when the app starts.
    private locationTrackingService: LocationTrackingService,
    private projectBoundaryTrackingService: ProjectBoundaryTrackingService,
    private alertService: AlertService,
    private debugSessionService: DebugSessionService,
    private odometerReadingMediaCleanupService: OdometerReadingMediaCleanupService,
    private chatbotService: ChatbotService,
  ) {
    this.configureSideMenu();

    // listen for login event to start sync
    const loginSuccessSubscription = this.eventBusService.on(AppEventType.LOGIN_SUCCESS, async () => this.loginSync());
    this.subscriptions.push(loginSuccessSubscription);

    const initialSyncDataAvailableSubscription = this.eventBusService.on(AppEventType.INITIAL_SYNC_DATA_AVAILABLE, () => this.syncService.startPolling());
    this.subscriptions.push(initialSyncDataAvailableSubscription);

    this.appStateService.getAppState().subscribe(appState => {
      if (appState.isActive) {
        this.syncTimeDataIfNetworkConnected();
      }
    });

    this.notificationsService.registerNotifications();

    this.notificationsService.notificationPerformed.subscribe(async notification => {
      if (notification.type === 'push') {
        const data = notification.data;
        if (data) {
          if (data.content.type === PUSH_NOTIFICATION_TYPES.RETURN_TIME) {
            const loading = await this.loadingController.create({ message: 'Syncing latest data...' });
            try {
              await loading.present();
              await this.timeService.syncTimeData();
            } catch {
              console.log('Error in sync');
            } finally {
              await loading.dismiss();
            }

            await this.router.navigate(['app/activityLog'], { replaceUrl: true, queryParams: { date: data.content.date } });
          }
        }
      } else if (notification.type === 'local') {
        const data = notification.data;
        const actionType = data.content.actionTypeId;

        switch (actionType) {
          case LOCAL_NOTIFICATION_ACTION_TYPE.ACTIVITY_LOG.toString():
            await this.router.navigate(['app/activityLog'], { replaceUrl: true });
            break;
          default:
            await this.router.navigate(['app/home'], { replaceUrl: true });
            break;
        }
      }
    });

    this.notificationsService.notificationReceived.subscribe(async notification => {
      if (notification.type === 'push') {
        const data = notification.data;
        if (data) {
          if (data.content.type === PUSH_NOTIFICATION_TYPES.SYSTEM) {
            this.router.routeReuseStrategy.shouldReuseRoute = () => false;
            this.router.navigate(['forgotpassword'], {
              queryParams: { changeKey: data.content.body },
            });
            this.router.onSameUrlNavigation = 'reload';
          }
          if (data.content.SavingMessage === 'true') {
            this.messageService.createMessage(data);
          }

          if (data.content.type === PUSH_NOTIFICATION_TYPES.RETURN_TIME) {
            this.timeService.syncTimeData();
          }
        }
      }
    });

    if (Capacitor.getPlatform() !== 'web') {
      const options: SetOptions = {
        value: 1,
      };
      TextZoom.set(options);
      this.getDeviceInfo();
    } else {
      //console.log('Inside the Web');
      this.appVersion = environment.version;
    }
    this.configureBackButton();
  }

  private async loginSync() {
    const isInitialSyncDataAvailable = await this.syncService.checkIsInitialSyncCompleted();
    const loading = await this.loadingController.create({ message: !isInitialSyncDataAvailable ? 'Performing initial sync. This may take a few minutes...' : 'Syncing latest data...' });
    await loading.present();
    try {
      await this.syncService.loginSync();
      this.eventBusService.emit({ type: AppEventType.INITIAL_SYNC_DATA_AVAILABLE, data: null });
    } catch {
      if (isInitialSyncDataAvailable) {
        this.notificationsService.showAlert({
          mode: 'ios',
          header: 'Attention',
          message: 'Some data did not sync correctly',
          backdropDismiss: false,
          buttons: [
            {
              text: 'Ok',
              role: 'confirm',
              handler: () => this.eventBusService.emit({ type: AppEventType.INITIAL_SYNC_DATA_AVAILABLE, data: null }),
            },
          ],
        });
      } else {
        this.notificationsService.showAlert({
          mode: 'ios',
          header: 'Error',
          message: `Initial sync failed, please try again. If the issue persists, please contact support at ${GlobalConstants.SUPPORT_EMAIL}.`,
          backdropDismiss: false,
          buttons: [
            { text: 'Log Out', handler: () => this.logout() },
            {
              text: 'Try Again',
              handler: async () => await this.loginSync(),
            },
          ],
        });
      }
    } finally {
      await loading.dismiss();
    }
  }

  async getDeviceInfo() {
    const appInfo = await App.getInfo();
    this.appVersion = appInfo.version;
  }

  async initializeApp() {
    if (Capacitor.getPlatform() !== 'web') {
      this.platform.ready().then(async () => {
        this.splashScreen.hide();
      });
    }
  }

  configureSideMenu() {
    this.appPages = [
      {
        title: 'Home',
        url: 'app',
        ionicIcon: 'home',
        hidden: this.sideMenuConfiguration.hideClockEntryItems,
      },
      {
        title: 'Activity Log',
        url: 'app/activityLog',
        ionicIcon: 'list',
        hidden: this.sideMenuConfiguration.hideClockEntryItems,
      },
      {
        title: 'Time History',
        url: 'app/timeClock',
        ionicIcon: 'timer',
        hidden: this.sideMenuConfiguration.hideClockEntryItems,
      },
      {
        title: 'Home',
        url: 'app/timecards',
        ionicIcon: 'home',
        hidden: this.sideMenuConfiguration.hideBlockEntryItems,
      },
      {
        title: 'Templates',
        url: 'app/templates',
        ionicIcon: 'reader',
        hidden: this.sideMenuConfiguration.hideBlockEntryItems,
      },
      {
        title: 'Team Time & Activity',
        url: 'app/teamproject',
        ionicIcon: 'people',
        hidden: this.sideMenuConfiguration.hideReviewTeamTime,
      },
      {
        title: 'Maps',
        url: 'app/maps',
        ionicIcon: 'map',
        hidden: this.sideMenuConfiguration.hideMap,
      },
      {
        title: 'Forms',
        url: 'app/forms',
        ionicIcon: 'document-text',
        hidden: false
      },
      {
        title: 'Virtual Badge',
        url: 'app/virtualBadge',
        ionicIcon: 'id-card',
        hidden: this.sideMenuConfiguration.hideVirtualBadge,
      },
    ];

    this.appInfoPages = [{
      title: 'Sync Center',
      url: 'syncCenter',
      hidden: false
    },
    {
      title: 'Privacy Policy',
      url: 'privacyPolicy',
      hidden: false
    },
    {
      title: 'Terms of Service',
      url: 'termsOfService',
      hidden: false,
    },
    {
      title: 'Tutorial',
      url: 'app/tutorial',
      hidden: this.sideMenuConfiguration.hideClockEntryItems
    }
    ];
  }

  configureBackButton() {
    this.platform.backButton.subscribeWithPriority(1, () => {
      if (!JSON.parse(localStorage.getItem('MODAL_ACTIVE'))) {
        if (this.routerOutlet.canGoBack()) {
          if (this.router.url === '/app/home') {
            this.notificationsService.showAlert({
              mode: 'ios',
              message: 'Do you want to close the app?',
              buttons: [
                {
                  text: 'No',
                  role: 'cancel',
                },
                {
                  text: 'Yes',
                  handler: () => {
                    navigator['app'].exitApp();
                  },
                },
              ],
            });
          } else {
            this.location.back();
          }
        } else {
          navigator['app'].exitApp();
        }
      }
    });
  }
  ngOnDestroy(): void {
    this.chatbotService.destroyChatbot();
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  async ngOnInit() {
    this.chatbotService.initializeChatbot();

    this.loadSubscriptions();

    this.loadPermissions()
    let applicationUser: any;
    try {
      applicationUser = JSON.parse(localStorage.getItem(GlobalConstants.USER_KEY));
    } catch (e) {
      return;
    }
    if (applicationUser) {
      if (Capacitor.getPlatform() !== 'web') {
        await this.getDeviceInfo();
      } else {
        this.appVersion = environment.version;
      }
      if (applicationUser.Email == null) {
        localStorage.clear();
      }
    }

    // Check if this is initial login and sync data is not available
    if (this.authService.isAuthenticated()) {
      const isInitialSyncDataAvailable = await this.syncService.checkIsInitialSyncCompleted();
      if (!isInitialSyncDataAvailable) {
        await this.loginSync();
      } else {
        this.syncTimeDataIfNetworkConnected();
        this.eventBusService.emit({ type: AppEventType.INITIAL_SYNC_DATA_AVAILABLE, data: null });
      }
    }
    this.initializeApp();
  }

  private loadSubscriptions() {
    const authEventsSubscription = this.eventBusService.on(AppEventType.LOGIN_SUCCESS, () => {
      this.loadPermissions();
      this.configureSideMenu();
    });
    this.subscriptions.push(authEventsSubscription);

    this.appUserTimeEntryMethodService.isClockTimeEntryUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isClockTimeUser => {
        this.sideMenuConfiguration.hideClockEntryItems = !isClockTimeUser;
        this.configureSideMenu();
      });

    this.appUserTimeEntryMethodService.isBlockTimeEntryUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isBlockTimeUser => {
        this.sideMenuConfiguration.hideBlockEntryItems = !isBlockTimeUser;
        this.configureSideMenu();
      });

    this.kioskModeService.isKioskModeEnabled$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isKioskModeEnabled => {
        this.sideMenuConfiguration.hideMap = isKioskModeEnabled;
        this.sideMenuConfiguration.hideVirtualBadge = isKioskModeEnabled;
        this.configureSideMenu();
      });
  }

  loadPermissions() {
    this.sideMenuConfiguration.hideReviewTeamTime = !(this.roleClaimService.checkHasVerifyPermission() || this.roleClaimService.checkHasApprovePermission());
  }

  async logout() {
    await this.authService.logout();
  }

  private syncTimeDataIfNetworkConnected() {
    const isNetworkAvailable = this.appStateService.getCurrentNetworkStatus();
    if (isNetworkAvailable) {
      this.timeService.syncTimeData();
    }
  }
}
